import { AR_AGING_BUCKET_LABELS } from "@common/types/reports/ar-aging";
import { ArAgingDetailResponse } from "@common/types/reports/types";
import { ArAgingDetailViewRow } from "./arAgingDetailViewTypes";

const GRAND_TOTAL_ROW_ID = "grand-total:ar-aging-detail";

/**
 * Converts an ArAgingDetailResponse into the nested row model for the
 * ViewTable runtime: one expandable "bucket" parent row per section (in
 * `AR_AGING_BUCKET_ORDER`, per the backend's own ordering — empty bucket
 * sections are never sent) wrapping its invoice "line" children, followed by
 * a trailing "bucketTotal" child (Open subtotal) and a trailing top-level
 * "grandTotal" row. Mirrors how P&L/Balance Sheet treat a section's
 * synthetic total and how GL nests an Ending Balance under an account.
 */
export function buildArAgingDetailRows(
    report: ArAgingDetailResponse,
): ArAgingDetailViewRow[] {
    const bucketRows: ArAgingDetailViewRow[] = report.sections.map(
        (section) => ({
            id: `bucket:${section.bucket}`,
            rowKind: "bucket",
            bucket: section.bucket,
            bucketLabel: AR_AGING_BUCKET_LABELS[section.bucket],
            subtotalAmount: section.subtotalAmount,
            subtotalOpen: section.subtotalOpen,
            children: [
                ...section.rows.map((row): ArAgingDetailViewRow => ({
                    id: `line:${row.invoiceId}:${section.bucket}:${row.classId ?? "none"}`,
                    rowKind: "line",
                    invoiceId: row.invoiceId,
                    date: row.date,
                    source: row.source,
                    reference: row.reference,
                    customerId: row.customerId,
                    customerName: row.customerName,
                    description: row.description,
                    classId: row.classId,
                    className: row.className,
                    dueDate: row.dueDate,
                    entityId: row.entityId,
                    entityName: row.entityName,
                    amount: row.amount,
                    open: row.open,
                })),
                {
                    id: `bucket-total:${section.bucket}`,
                    rowKind: "bucketTotal",
                    subtotalAmount: section.subtotalAmount,
                    subtotalOpen: section.subtotalOpen,
                },
            ],
        }),
    );

    return [
        ...bucketRows,
        {
            id: GRAND_TOTAL_ROW_ID,
            rowKind: "grandTotal",
            subtotalAmount: report.grandTotalAmount,
            subtotalOpen: report.grandTotalOpen,
        },
    ];
}

export function getArAgingDetailInvoiceIds(
    report: ArAgingDetailResponse,
): string[] {
    return [
        ...new Set(
            report.sections.flatMap((section) =>
                section.rows.map((row) => row.invoiceId),
            ),
        ),
    ];
}

export function getArAgingDetailExpandedState(
    rows: ArAgingDetailViewRow[],
): Record<string, boolean> {
    return Object.fromEntries(
        rows
            .filter((row) => row.rowKind === "bucket")
            .map((row) => [row.id, true] as const),
    );
}
